export enum ConsentType {
  TERMS_OF_SERVICE = "terms_of_service",
  PRIVACY_POLICY = "privacy_policy",
  AGE_VERIFICATION = "age_verification",
  AI_PROCESSING = "ai_processing",
  DATA_SHARING = "data_sharing",
  YOUTUBE_OAUTH = "youtube_oauth",
  CAMPAIGN_TERMS = "campaign_terms",
  PRODUCER_AUTHORIZATION = "producer_authorization"
}

export enum ConsentStatus {
  PENDING = "pending",
  ACCEPTED = "accepted",
  DECLINED = "declined",
  REVOKED = "revoked"
}

export interface ConsentMetadata {
  ip_address?: string;
  user_agent?: string;
  document_version?: string;
  scroll_percentage?: number;
  time_spent_reading?: number; // seconds
  campaign_id?: string;
  project_id?: string;
  collaborator_email?: string;
  oauth_scope?: string;
  additional_data?: Record<string, any>;
}

export interface ConsentRecord {
  _id: string;
  user_id: string;
  consent_type: ConsentType;
  status: ConsentStatus;
  metadata?: ConsentMetadata;
  created_at: string;
  updated_at: string;
  expires_at?: string;
}

export interface ConsentRecordCreate {
  consent_type: ConsentType;
  status: ConsentStatus;
  metadata?: ConsentMetadata;
  expires_at?: string;
}

export interface ConsentRecordUpdate {
  status?: ConsentStatus;
  metadata?: ConsentMetadata;
  expires_at?: string;
}

export interface ConsentSummary {
  user_id: string;
  consents: Record<ConsentType, ConsentRecord>;
  missing_consents: ConsentType[];
  expired_consents: ConsentType[];
  all_required_consents_given: boolean;
}

export interface ConsentValidationResult {
  is_valid: boolean;
  missing_consents: ConsentType[];
  expired_consents: ConsentType[];
  required_actions: string[];
}

// Consent flow configuration
export interface ConsentFlowConfig {
  type: ConsentType;
  title: string;
  description: string;
  documentContent?: string;
  documentUrl?: string;
  requiresScroll?: boolean;
  requiresTimeSpent?: boolean;
  minTimeSpent?: number; // seconds
  checkboxText: string;
  acceptButtonText?: string;
  declineButtonText?: string;
  isRequired: boolean;
  documentVersion?: string;
}

// Legal document configurations
export const CONSENT_FLOWS: Record<ConsentType, ConsentFlowConfig> = {
  [ConsentType.TERMS_OF_SERVICE]: {
    type: ConsentType.TERMS_OF_SERVICE,
    title: "Terms of Service",
    description: "Please read and accept our Terms of Service to continue using Artie.",
    documentUrl: "/legal/terms-of-service",
    requiresScroll: false,
    requiresTimeSpent: false,
    minTimeSpent: 0,
    checkboxText: "I have read and agree to the Terms of Service",
    acceptButtonText: "Accept Terms",
    declineButtonText: "Decline",
    isRequired: true,
    documentVersion: "v1.0"
  },
  [ConsentType.PRIVACY_POLICY]: {
    type: ConsentType.PRIVACY_POLICY,
    title: "Privacy Policy",
    description: "Please review our Privacy Policy to understand how we handle your data.",
    documentUrl: "/legal/privacy-policy",
    requiresScroll: false,
    requiresTimeSpent: false,
    checkboxText: "I have read and accept the Privacy Policy",
    acceptButtonText: "Accept",
    declineButtonText: "Decline",
    isRequired: true,
    documentVersion: "v1.0"
  },
  [ConsentType.AGE_VERIFICATION]: {
    type: ConsentType.AGE_VERIFICATION,
    title: "Age Verification",
    description: "You must be at least 18 years old to use this platform.",
    checkboxText: "I confirm I am at least 18 years old",
    acceptButtonText: "Confirm",
    isRequired: true
  },
  [ConsentType.AI_PROCESSING]: {
    type: ConsentType.AI_PROCESSING,
    title: "AI Processing Acknowledgment",
    description: "We use AI to analyze your content and provide recommendations. This helps us deliver better insights for your projects.",
    documentContent: `
      <h3>AI Processing Disclosure</h3>
      <p>Artie uses artificial intelligence and machine learning technologies to:</p>
      <ul>
        <li>Analyze your scripts and content for brand integration opportunities</li>
        <li>Generate personalized recommendations for your projects</li>
        <li>Calculate your Artie Score based on various performance metrics</li>
        <li>Provide insights on audience engagement and content optimization</li>
      </ul>
      <p>Your content is processed securely and in accordance with our Privacy Policy. We do not share your content with third parties without your explicit consent.</p>
    `,
    checkboxText: "I understand and consent to AI processing of my content",
    acceptButtonText: "Accept",
    isRequired: true
  },
  [ConsentType.DATA_SHARING]: {
    type: ConsentType.DATA_SHARING,
    title: "Data Sharing Consent",
    description: "Required before your creator content becomes visible to brands. This enables brand partnership opportunities.",
    checkboxText: "I consent to sharing my data with brand partners and collaborators",
    acceptButtonText: "Allow Sharing",
    declineButtonText: "Keep Private",
    isRequired: true
  },
  [ConsentType.YOUTUBE_OAUTH]: {
    type: ConsentType.YOUTUBE_OAUTH,
    title: "YouTube Integration",
    description: "Connect your YouTube channel to get personalized insights and recommendations.",
    documentContent: `
      <h3>YouTube Data Access</h3>
      <p>By connecting your YouTube account, you grant Artie permission to:</p>
      <ul>
        <li>Access your channel statistics and analytics</li>
        <li>Read your video metadata and performance data</li>
        <li>Analyze your audience demographics and engagement metrics</li>
        <li>Generate insights based on your content performance</li>
      </ul>
      <p>We will only access data necessary for providing our services. You can disconnect your account at any time.</p>
    `,
    checkboxText: "I authorize Artie to access my YouTube data",
    acceptButtonText: "Connect YouTube",
    declineButtonText: "Skip",
    isRequired: false
  },
  [ConsentType.CAMPAIGN_TERMS]: {
    type: ConsentType.CAMPAIGN_TERMS,
    title: "Campaign Terms",
    description: "Accept the terms and conditions for this specific campaign.",
    checkboxText: "I agree to the campaign terms and conditions",
    acceptButtonText: "Accept Campaign",
    declineButtonText: "Decline",
    isRequired: true
  },
  [ConsentType.PRODUCER_AUTHORIZATION]: {
    type: ConsentType.PRODUCER_AUTHORIZATION,
    title: "Producer Authorization",
    description: "Authorize a producer or collaborator to work on your project.",
    documentContent: `
      <h3>Producer Authorization Agreement</h3>
      <p>By providing this authorization, you:</p>
      <ul>
        <li>Grant the specified producer access to your project details</li>
        <li>Allow them to make recommendations and suggestions</li>
        <li>Acknowledge that this is an offline business relationship</li>
        <li>Understand that Artie facilitates but does not manage this collaboration</li>
      </ul>
      <p>This authorization can be revoked at any time by removing the collaborator from your project.</p>
    `,
    checkboxText: "I authorize this producer to collaborate on my project",
    acceptButtonText: "Authorize",
    declineButtonText: "Decline",
    isRequired: true
  }
};

// Required consents for different actions
export const REQUIRED_CONSENTS = {
  basic_access: [
    ConsentType.TERMS_OF_SERVICE,
    ConsentType.PRIVACY_POLICY,
    ConsentType.AGE_VERIFICATION,
    ConsentType.AI_PROCESSING,
    ConsentType.DATA_SHARING
  ],
  brand_basic_access: [
    ConsentType.TERMS_OF_SERVICE,
    ConsentType.PRIVACY_POLICY,
    ConsentType.AI_PROCESSING,
    ConsentType.DATA_SHARING
  ],
  data_sharing: [ConsentType.DATA_SHARING],
  youtube_integration: [ConsentType.YOUTUBE_OAUTH],
  campaign_participation: [ConsentType.CAMPAIGN_TERMS],
  producer_collaboration: [ConsentType.PRODUCER_AUTHORIZATION]
};

export function getRequiredConsentsForAction(action: string): ConsentType[] {
  return REQUIRED_CONSENTS[action as keyof typeof REQUIRED_CONSENTS] || [];
}

export function getAllRequiredConsents(): ConsentType[] {
  return REQUIRED_CONSENTS.basic_access;
}

export function getRequiredConsentsForUserType(userType?: string, userRole?: string, brandId?: string | null): ConsentType[] {
  // Check if user is a brand user based on role, user_type, or brand_id
  const isBrandUser = userRole === 'admin' || userRole === 'brand_user' || userType === 'brand' || brandId;
  
  console.log('getRequiredConsentsForUserType Debug - userType:', userType, 'userRole:', userRole, 'brandId:', brandId);
  console.log('getRequiredConsentsForUserType Debug - isBrandUser:', isBrandUser);
  
  if (isBrandUser) {
    console.log('getRequiredConsentsForUserType Debug - Returning brand_basic_access:', REQUIRED_CONSENTS.brand_basic_access);
    return REQUIRED_CONSENTS.brand_basic_access;
  }
  
  console.log('getRequiredConsentsForUserType Debug - Returning basic_access:', REQUIRED_CONSENTS.basic_access);
  return REQUIRED_CONSENTS.basic_access;
}